import React from 'react';
import { useController } from 'react-hook-form';
import { t } from '@lingui/macro';

import { Box, Paragraph, Text } from '@rover/kibble/core';
import { ConfigureFieldInterface, ZodSchemaOrEffects } from '@rover/kibble/forms';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import { CountryChangeAlert, useCountryName } from '@rover/shared/js/components/KibbleAddressForm';

import { AddressType } from '../../LocationInput.constants';

type Props<Z extends ZodSchemaOrEffects> = {
  countryCodeField: ConfigureFieldInterface<Z>;
  countryCheckboxesField: ConfigureFieldInterface<Z>;
  isHomeAddressChecked: boolean;
  isAddressDifferentFromHome: boolean;
  isProvider: boolean;
  addressType: AddressType;
};

export default function AddressAlerts<Z extends ZodSchemaOrEffects>({
  countryCodeField,
  countryCheckboxesField,
  isHomeAddressChecked,
  isAddressDifferentFromHome,
  isProvider,
  addressType,
}: Props<Z>): JSX.Element {
  const { locale } = useI18nContext();

  const { fieldState: countryFieldState, field: countryFieldValue } = useController({
    control: countryCodeField.control,
    name: countryCodeField.name,
  });

  const { countryName } = useCountryName(locale, countryFieldValue.value);

  return (
    <>
      {/* New home address alert - Only applies for owners with new temporary addresses */}
      {isHomeAddressChecked &&
        isAddressDifferentFromHome &&
        addressType === AddressType.Temporary &&
        !isProvider && (
          <Box mt="4x" background="highlight" p="4x" borderRadius="secondary" pl="6x">
            <Text size="200" fontWeight="semibold" mb="1x">
              {t`New home address`}
            </Text>
            <Paragraph size="100" mb="0x" mt="4x">
              {t`This address is different from the home address saved to your profile. Do you want to replace your current home address with this one?`}
            </Paragraph>
          </Box>
        )}

      {/* Country change alert - Only applies when owners change their home country */}
      {isHomeAddressChecked &&
        countryFieldState.isDirty &&
        addressType === AddressType.Home &&
        !isProvider && (
          <CountryChangeAlert
            countryName={countryName}
            countryCheckboxesField={countryCheckboxesField}
          />
        )}

      {/* Temporary address alert - Only applied when providers try to use a new address */}
      {isAddressDifferentFromHome && isProvider && (
        <Box mt="4x" background="accent" p="4x" borderRadius="secondary" pl="6x">
          <Text size="200" fontWeight="semibold" mb="1x">
            {t`Searching in a different location?`}
          </Text>
          <Paragraph size="100" mb="0x" mt="4x">
            {t`We noticed you're looking in a different location than your saved home address. We'll treat this as a temporary address for now.`}
          </Paragraph>
          <Paragraph size="100" mb="0x" mt="4x">
            {t`If you want to update your home address to this new location, you can change it in your settings.`}
          </Paragraph>
        </Box>
      )}
    </>
  );
}
